/**
 * A snap's caption and emojis, drawn over its picture.
 *
 * Their places are fractions of the picture, so they are drawn on a stage the
 * picture's own shape -- not on the screen, where a letterboxed photo leaves
 * bars that would move everything the sender placed. Their sizes are fractions
 * of its width, so a caption covers as much of a photo in the viewer as it did
 * in the smaller preview it was placed on.
 */
import type { CSSProperties, ReactNode } from 'react';
import { SNAP_FRAME_RATIO, snapDecorOf } from '../../shared/snapDecor';
import type { SnapDecor, SnapSticker } from '../../shared/types';
import { artUrl } from '../../shared/stickerArt';
import { Caption, styleFor } from './Caption';

/** Before a picture has said what shape it is: a phone photo, upright. */
const FALLBACK_RATIO = 3 / 4;

/** Width over height, or the fallback when either is missing. */
export function ratioOf(width: number | undefined, height: number | undefined): number {
  return width && height && width > 0 && height > 0 ? width / height : FALLBACK_RATIO;
}

/**
 * What to draw on a GiviSnap, from what its author wrote to it.
 *
 * The author writes these from their own phone, and rules cannot check each
 * emoji in a list, so they are put through the same check a snap's go through
 * on the server before anybody's screen draws them. A list that fails loses
 * its emojis and keeps its words.
 */
export function decorOfPost(
  post: Pick<SnapDecor, 'caption' | 'captionAt' | 'captionStyle' | 'stickers'>,
): SnapDecor {
  const words = { caption: post.caption, captionAt: post.captionAt, captionStyle: post.captionStyle };
  return snapDecorOf({ ...words, stickers: post.stickers }) ?? snapDecorOf(words) ?? {};
}

/** Whether there is anything to draw. */
export function hasDecor(decor: SnapDecor): boolean {
  return Boolean(decor.caption) || Boolean(decor.stickers?.length);
}

/**
 * What fills the screen round a picture that is not the screen's shape: the
 * picture itself, blown up and blurred, so the bars are its own colours rather
 * than black. For a photo, the photo.
 */
export function PhotoBackdrop({ src }: { src: string }) {
  return <img className="snap-backdrop" src={src} alt="" aria-hidden="true" draggable={false} />;
}

/** Small, because it is only ever seen blurred. */
const BACKDROP_PX = 48;

/**
 * For a clip, one still of it, painted onto a canvas the moment it has a frame
 * to show: a second copy of the clip playing behind the first would decode it
 * twice on a phone for the sake of a blur.
 *
 * A clip from the media gateway taints the canvas, which only stops it being
 * read back; it is drawn all the same.
 */
export function paintBackdrop(canvas: HTMLCanvasElement | null, video: HTMLVideoElement): void {
  const pen = canvas?.getContext('2d');
  if (!canvas || !pen) return;
  canvas.width = BACKDROP_PX;
  canvas.height = BACKDROP_PX;
  pen.drawImage(video, 0, 0, BACKDROP_PX, BACKDROP_PX);
}

/**
 * The picture's own shape, with what is on it. `roomy` when something is
 * placed off the picture, in the fill round it: the stage is then sized so
 * the whole frame round it fits on the screen (SNAP_FRAME_RATIO), not just
 * the picture, or a caption above a photo on a laptop would be cut off.
 */
export function SnapStage({ ratio, roomy = false, children }: { ratio: number; roomy?: boolean; children: ReactNode }) {
  const style = { '--ar': String(ratio), ...(roomy ? { '--fit': String(Math.min(ratio, SNAP_FRAME_RATIO)) } : {}) };
  return (
    <span className="snap-stage" style={style as CSSProperties}>
      {children}
    </span>
  );
}

/** An emoji, or a drawing (shared/stickerArt.ts) the same width an emoji is at its size. */
export function Sticker({ sticker }: { sticker: SnapSticker }) {
  if ('art' in sticker) {
    return (
      <span className="snap-sticker snap-sticker--art" style={styleFor(sticker.at)}>
        <img src={artUrl(sticker.art)} alt="" draggable={false} />
      </span>
    );
  }
  return (
    <span className="snap-sticker" style={styleFor(sticker.at)}>
      {sticker.emoji}
    </span>
  );
}

/** As everybody else sees it: in place, and not taking a single press. */
export function SnapDecorLayer({ decor }: { decor: SnapDecor }) {
  if (!hasDecor(decor)) return null;
  return (
    <span className="snap-decor" aria-hidden={decor.caption ? undefined : true}>
      {decor.stickers?.map((sticker, index) => (
        <Sticker key={index} sticker={sticker} />
      ))}
      {decor.caption && <Caption text={decor.caption} at={decor.captionAt} look={decor.captionStyle} />}
    </span>
  );
}
